import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { settingsAPI, authAPI } from '../utils/api';
import { formatDate, formatDateTime } from '../lib/date';
import DataTables from './DataTables';

const API_BASE = '/api';

function Settings({ userRole }) {
  const [activeTab, setActiveTab] = useState('database');
  const [dbStatus, setDbStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(null);
  const [showChangeRole, setShowChangeRole] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'client', firstName: '', lastName: '', email: '', forcePasswordReset: false });
  const [newPassword, setNewPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [azureConfigured, setAzureConfigured] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Notifications tab state
  const [emailLog, setEmailLog] = useState([]);
  const [emailLogPage, setEmailLogPage] = useState(1);
  const [emailLogPagination, setEmailLogPagination] = useState(null);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(null);
  const [expandedLogRow, setExpandedLogRow] = useState(null);

  // Feedback tab state
  const [feedbackLog, setFeedbackLog] = useState([]);
  const [feedbackLogPage, setFeedbackLogPage] = useState(1);
  const [feedbackLogPagination, setFeedbackLogPagination] = useState(null);
  const [feedbackLogLoading, setFeedbackLogLoading] = useState(false);

  // Versions tab state
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [newVersion, setNewVersion] = useState({ version: '', notes: '' });

  // Audit tab state
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogPage, setAuditLogPage] = useState(1);
  const [auditLogPagination, setAuditLogPagination] = useState(null);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [auditActions, setAuditActions] = useState([]);
  const [auditSortBy, setAuditSortBy] = useState('timestamp');
  const [auditSortDir, setAuditSortDir] = useState('desc');

  // User row action menu
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPos, setMenuPos] = useState(null);

  // Roles matrix tab state
  const [rolePerms, setRolePerms] = useState(null); // { items, roles, matrix }
  const [rolePermsLoading, setRolePermsLoading] = useState(false);
  const [rolePermsSaving, setRolePermsSaving] = useState(false);
  const [rolePermsDirty, setRolePermsDirty] = useState(false);

  const isSysAdmin = userRole?.toLowerCase() === 'sysadmin';

  // Fetch database status
  const checkDatabaseStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/settings/db-status`, { withCredentials: true });
      setDbStatus(response.data);
    } catch (err) {
      if (err.response && err.response.data) {
        setDbStatus(err.response.data);
      } else {
        setError('Failed to check database status');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching users...');
      const response = await settingsAPI.getUsers();
      console.log('Users response:', response.data);
      setUsers(response.data);
    } catch (err) {
      console.error('Fetch users error:', err);
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const response = await settingsAPI.getRoles();
      setRoles(response.data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  // Check Azure configuration
  const checkAzureConfig = async () => {
    try {
      const response = await authAPI.getAzureConfig();
      setAzureConfigured(response.data.enabled);
    } catch (err) {
      console.error('Failed to check Azure config:', err);
    }
  };

  // Add user
  const handleAddUser = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      const response = await settingsAPI.addUser(newUser);
      const msg = response.data?.welcomeEmailSent
        ? 'User added successfully. Welcome email with password reset link has been sent.'
        : 'User added successfully';
      setSuccess(msg);
      setNewUser({ username: '', password: '', role: 'client', firstName: '', lastName: '', email: '', forcePasswordReset: false });
      setShowAddUser(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add user');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    setError(null);
    setSuccess(null);
    try {
      await settingsAPI.deleteUser(userId);
      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  // Resend invite / password reset link
  const handleResendInvite = async (userId, username, email) => {
    if (!window.confirm(`Send a password reset link to ${email}?`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      const response = await settingsAPI.resendInvite(userId);
      setSuccess(response.data?.message || `Password reset link sent to ${email}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send password reset link');
    }
  };

  // Change password
  const handleChangePassword = async (e, userId) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      await settingsAPI.updateUserPassword(userId, newPassword);
      setSuccess('Password updated successfully');
      setNewPassword('');
      setShowChangePassword(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password');
    }
  };

  // Change role
  const handleChangeRole = async (e, userId) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      await settingsAPI.updateUserRole(userId, selectedRole);
      setSuccess('Role updated successfully');
      setSelectedRole('');
      setShowChangeRole(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    }
  };

  // Suspend / reactivate a user account
  const handleToggleSuspend = async (user) => {
    const next = !user.suspended;
    if (!window.confirm(`${next ? 'Suspend' : 'Reactivate'} user "${user.username}"?`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      const response = await settingsAPI.setUserSuspended(user.id, next);
      setSuccess(response.data?.message || `User ${next ? 'suspended' : 'reactivated'}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account status');
    }
  };

  // Fetch role → screen/tab access matrix
  const fetchRolePermissions = async () => {
    setRolePermsLoading(true);
    setError(null);
    try {
      const response = await settingsAPI.getRolePermissions();
      setRolePerms(response.data);
      setRolePermsDirty(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch role permissions');
    } finally {
      setRolePermsLoading(false);
    }
  };

  // Toggle a single cell in the matrix
  const toggleRolePerm = (role, key) => {
    setRolePerms((prev) => {
      if (!prev) return prev;
      const roleRow = { ...(prev.matrix[role] || {}) };
      roleRow[key] = !roleRow[key];
      return { ...prev, matrix: { ...prev.matrix, [role]: roleRow } };
    });
    setRolePermsDirty(true);
  };

  // Save the matrix
  const handleSaveRolePermissions = async () => {
    setRolePermsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await settingsAPI.saveRolePermissions(rolePerms.matrix);
      setSuccess('Role access saved. Affected users will see the change on their next page load (or after re-login if their role also changed).');
      setRolePermsDirty(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save role permissions');
    } finally {
      setRolePermsSaving(false);
    }
  };

  // Fetch email log
  const fetchEmailLog = async (page = 1) => {
    setEmailLogLoading(true);
    try {
      const response = await settingsAPI.getEmailLog(page);
      setEmailLog(response.data.data);
      setEmailLogPagination(response.data.pagination);
      setEmailLogPage(page);
    } catch (err) {
      console.error('Failed to fetch email log:', err);
      setError(err.response?.data?.error || 'Failed to fetch email log');
    } finally {
      setEmailLogLoading(false);
    }
  };

  // Check email configuration status
  const checkEmailConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE}/email/status`, { withCredentials: true });
      console.log('Email status response:', response.data);
      setEmailConfigured(response.data.configured);
    } catch (err) {
      console.error('Failed to check email config:', err);
      // Don't show misleading banner on network errors
      setEmailConfigured(null);
    }
  };

  // Fetch feedback log
  const fetchFeedbackLog = async (page = 1) => {
    setFeedbackLogLoading(true);
    try {
      const response = await settingsAPI.getFeedbackLog(page);
      setFeedbackLog(response.data.data);
      setFeedbackLogPagination(response.data.pagination);
      setFeedbackLogPage(page);
    } catch (err) {
      console.error('Failed to fetch feedback log:', err);
      setError(err.response?.data?.error || 'Failed to fetch feedback log');
    } finally {
      setFeedbackLogLoading(false);
    }
  };

  // Fetch audit log
  const fetchAuditLog = async (params = {}) => {
    setAuditLogLoading(true);
    try {
      const page = params.page || auditLogPage;
      const response = await settingsAPI.getAuditLog({
        page,
        search: params.search !== undefined ? params.search : auditSearch,
        action: params.action !== undefined ? params.action : auditActionFilter,
        sortBy: params.sortBy || auditSortBy,
        sortDir: params.sortDir || auditSortDir,
      });
      setAuditLog(response.data.data);
      setAuditLogPagination(response.data.pagination);
      setAuditLogPage(page);
      if (response.data.actions) {
        setAuditActions(response.data.actions);
      }
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      setError(err.response?.data?.error || 'Failed to fetch audit log');
    } finally {
      setAuditLogLoading(false);
    }
  };

  // Fetch versions
  const fetchVersions = async () => {
    setVersionsLoading(true);
    try {
      const response = await settingsAPI.getVersions();
      setVersions(response.data);
    } catch (err) {
      console.error('Failed to fetch versions:', err);
      setError(err.response?.data?.error || 'Failed to fetch versions');
    } finally {
      setVersionsLoading(false);
    }
  };

  // Add version
  const handleAddVersion = async (e) => {
    e.preventDefault();
    if (!newVersion.version.trim()) return;
    setError(null);
    setSuccess(null);
    try {
      await settingsAPI.addVersion(newVersion);
      setNewVersion({ version: '', notes: '' });
      setSuccess('Version added successfully');
      fetchVersions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add version');
    }
  };

  // Delete version
  const handleDeleteVersion = async (id) => {
    if (!window.confirm('Delete this version entry?')) return;
    setError(null);
    try {
      await settingsAPI.deleteVersion(id);
      setSuccess('Version deleted');
      fetchVersions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete version');
    }
  };

  // Sync Azure users
  const handleSyncAzure = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await settingsAPI.syncAzureUsers();
      setSuccess(`${response.data.message}. Added: ${response.data.results.added}, Updated: ${response.data.results.updated}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync users from Azure AD');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'database') {
      checkDatabaseStatus();
    } else if (activeTab === 'users') {
      fetchUsers();
      fetchRoles();
      checkAzureConfig();
    } else if (activeTab === 'notifications') {
      fetchEmailLog(1);
      checkEmailConfig();
    } else if (activeTab === 'feedback') {
      fetchFeedbackLog(1);
    } else if (activeTab === 'audit') {
      fetchAuditLog({ page: 1 });
    } else if (activeTab === 'versions') {
      fetchVersions();
    } else if (activeTab === 'roles') {
      fetchRolePermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Close the (fixed-positioned) actions menu on scroll/resize/Escape so it
  // never floats away from its button. Capture-phase scroll catches scrolling
  // inside any nested container too.
  useEffect(() => {
    if (openActionMenu === null) return undefined;
    const close = () => setOpenActionMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [openActionMenu]);

  const getRoleBadgeClass = (role) => {
    switch (role?.toLowerCase()) {
      case 'sysadmin':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'client':
        return 'bg-blue-100 text-blue-800';
      case 'contractor':
        return 'bg-green-100 text-green-800';
      case 'dre':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAuthTypeBadge = (authType) => {
    if (authType === 'azure') {
      return (
        <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
          Azure AD
        </span>
      );
    }
    return (
      <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600">
        Local
      </span>
    );
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Settings</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('database')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'database'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Database Connection
          </button>
          {isSysAdmin && (
            <button
              onClick={() => setActiveTab('data')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'data'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Data
            </button>
          )}
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            User Management
          </button>
          {isSysAdmin && (
            <button
              onClick={() => setActiveTab('roles')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'roles'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Roles
            </button>
          )}
          {isSysAdmin && (
            <button
              onClick={() => setActiveTab('notifications')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'notifications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notifications
            </button>
          )}
          {isSysAdmin && (
            <button
              onClick={() => setActiveTab('feedback')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'feedback'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Feedback
            </button>
          )}
          {isSysAdmin && (
            <button
              onClick={() => setActiveTab('audit')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit Log
            </button>
          )}
          {isSysAdmin && (
            <button
              onClick={() => setActiveTab('versions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'versions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Versions
            </button>
          )}
        </nav>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-xl">&times;</span>
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-xl">&times;</span>
          </button>
        </div>
      )}

      {/* Data Tab */}
      {activeTab === 'data' && isSysAdmin && (
        <DataTables />
      )}

      {/* Database Tab */}
      {activeTab === 'database' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Database Connection Status</h2>
            <button
              onClick={checkDatabaseStatus}
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50"
            >
              {loading ? 'Checking...' : 'Refresh Status'}
            </button>
          </div>

          {dbStatus && (
            <div className={`border-l-4 p-4 ${dbStatus.connected ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
              <div className="flex items-center mb-3">
                <div className={`w-3 h-3 rounded-full mr-3 ${dbStatus.connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <h3 className="text-lg font-semibold">
                  {dbStatus.connected ? 'Connected' : 'Not Connected'}
                </h3>
              </div>

              {dbStatus.connected ? (
                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="font-semibold text-gray-700">Server:</div>
                    <div className="text-gray-900">{dbStatus.server}</div>

                    <div className="font-semibold text-gray-700">Database:</div>
                    <div className="text-gray-900">{dbStatus.database}</div>

                    <div className="font-semibold text-gray-700">Response Time:</div>
                    <div className="text-gray-900">{dbStatus.responseTime}</div>

                    <div className="font-semibold text-gray-700">Last Checked:</div>
                    <div className="text-gray-900">{formatDateTime(dbStatus.timestamp)}</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="font-semibold text-gray-700">Error Type:</div>
                      <div className="text-red-700 font-semibold">{dbStatus.errorType}</div>

                      <div className="font-semibold text-gray-700">Server:</div>
                      <div className="text-gray-900">{dbStatus.server}</div>

                      <div className="font-semibold text-gray-700">Database:</div>
                      <div className="text-gray-900">{dbStatus.database}</div>

                      {dbStatus.clientIp && (
                        <>
                          <div className="font-semibold text-gray-700">Your IP Address:</div>
                          <div className="text-gray-900 font-mono">{dbStatus.clientIp}</div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-white rounded border border-red-200">
                    <h4 className="font-semibold text-red-800 mb-2">How to Fix:</h4>
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{dbStatus.suggestion}</pre>
                  </div>

                  {dbStatus.errorMessage && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                        Technical Details
                      </summary>
                      <div className="mt-2 p-3 bg-gray-100 rounded text-xs">
                        <div className="font-mono text-gray-700">{dbStatus.errorMessage}</div>
                        {dbStatus.errorCode && (
                          <div className="mt-1 text-gray-600">Error Code: {dbStatus.errorCode}</div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">User Management</h2>
            <div className="flex space-x-2">
              {isSysAdmin && azureConfigured && (
                <button
                  onClick={handleSyncAzure}
                  disabled={syncing}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50 flex items-center"
                >
                  {syncing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Sync Azure AD
                    </>
                  )}
                </button>
              )}
              <button
                onClick={() => setShowAddUser(!showAddUser)}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm"
              >
                {showAddUser ? 'Cancel' : 'Add Local User'}
              </button>
            </div>
          </div>

          {/* Azure AD info banner */}
          {isSysAdmin && azureConfigured && (
            <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="font-medium">Azure AD Integration Active</p>
                <p className="text-sm">Click "Sync Azure AD" to import users from your configured security group. New users will be assigned the Client role by default.</p>
              </div>
            </div>
          )}

          {/* Add User Form */}
          {showAddUser && (
            <form onSubmit={handleAddUser} className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
              <h3 className="text-lg font-semibold mb-3">Add New Local User</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                  <input
                    type="text"
                    required
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input
                    type="password"
                    required
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter email"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newUser.forcePasswordReset}
                    onChange={(e) => setNewUser({ ...newUser, forcePasswordReset: e.target.checked })}
                    disabled={!newUser.email}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span className={`text-sm font-medium ${!newUser.email ? 'text-gray-400' : 'text-gray-700'}`}>
                    Require password reset on first login
                  </span>
                  <span className="text-xs text-gray-400">
                    — sends a welcome email with a password reset link
                  </span>
                </label>
                <button
                  type="submit"
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm"
                >
                  Create User
                </button>
              </div>
            </form>
          )}

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : user.username}
                              {getAuthTypeBadge(user.authType)}
                              {user.suspended && (
                                <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                  Suspended
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{user.email || user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClass(user.role)}`}>
                          {user.roleDisplay || user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={(e) => {
                            if (openActionMenu === user.id) { setOpenActionMenu(null); return; }
                            const r = e.currentTarget.getBoundingClientRect();
                            const menuHeight = 236; // ~5 items + divider
                            const right = Math.max(8, window.innerWidth - r.right);
                            const openUp = r.bottom + menuHeight > window.innerHeight - 8 && r.top > menuHeight;
                            setMenuPos(openUp
                              ? { bottom: window.innerHeight - r.top + 4, right }
                              : { top: r.bottom + 4, right });
                            setOpenActionMenu(user.id);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                        >
                          Actions
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {openActionMenu === user.id && menuPos && createPortal(
                          <>
                            <div className="fixed inset-0 z-[9998]" onClick={() => setOpenActionMenu(null)} />
                            <div
                              className="fixed z-[9999] w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-left"
                              style={{
                                ...(menuPos.top != null ? { top: menuPos.top } : { bottom: menuPos.bottom }),
                                right: menuPos.right,
                              }}
                            >
                              <button
                                onClick={() => {
                                  setOpenActionMenu(null);
                                  setShowChangeRole(user.id);
                                  setSelectedRole(user.role);
                                  setShowChangePassword(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                Change Role
                              </button>
                              {(() => {
                                const azure = user.authType === 'azure';
                                return (
                                  <button
                                    onClick={() => {
                                      if (azure) return;
                                      setOpenActionMenu(null);
                                      setShowChangePassword(user.id);
                                      setShowChangeRole(null);
                                    }}
                                    disabled={azure}
                                    title={azure ? 'Azure AD users sign in via Microsoft SSO — no local password to change' : ''}
                                    className={`w-full text-left px-4 py-2 text-sm ${azure ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
                                  >
                                    Change Password
                                  </button>
                                );
                              })()}
                              {(() => {
                                const azure = user.authType === 'azure';
                                const disabled = azure || !user.email;
                                const title = azure
                                  ? 'Azure AD users sign in via Microsoft SSO'
                                  : !user.email ? 'No email address on file for this user' : `Send an access / password-reset link to ${user.email}`;
                                return (
                                  <button
                                    onClick={() => {
                                      if (disabled) return;
                                      setOpenActionMenu(null);
                                      handleResendInvite(user.id, user.username, user.email);
                                    }}
                                    disabled={disabled}
                                    title={title}
                                    className={`w-full text-left px-4 py-2 text-sm ${disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
                                  >
                                    Send User Invite
                                  </button>
                                );
                              })()}
                              <button
                                onClick={() => {
                                  setOpenActionMenu(null);
                                  handleToggleSuspend(user);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                {user.suspended ? 'Reactivate' : 'Suspend'}
                              </button>
                              <div className="my-1 border-t border-gray-100" />
                              <button
                                onClick={() => {
                                  setOpenActionMenu(null);
                                  handleDeleteUser(user.id, user.username);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          </>,
                          document.body
                        )}
                      </td>
                    </tr>
                    {showChangeRole === user.id && (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 bg-purple-50">
                          <form onSubmit={(e) => handleChangeRole(e, user.id)} className="flex items-center space-x-3">
                            <label className="text-sm font-medium text-gray-700">New Role:</label>
                            <select
                              value={selectedRole}
                              onChange={(e) => setSelectedRole(e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              {roles.map((role) => (
                                <option key={role.value} value={role.value}>
                                  {role.label} - {role.description}
                                </option>
                              ))}
                            </select>
                            <button
                              type="submit"
                              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded text-sm"
                            >
                              Update
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowChangeRole(null);
                                setSelectedRole('');
                              }}
                              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded text-sm"
                            >
                              Cancel
                            </button>
                          </form>
                        </td>
                      </tr>
                    )}
                    {showChangePassword === user.id && (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 bg-gray-50">
                          <form onSubmit={(e) => handleChangePassword(e, user.id)} className="flex items-center space-x-3">
                            <label className="text-sm font-medium text-gray-700">New Password:</label>
                            <input
                              type="password"
                              required
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter new password"
                            />
                            <button
                              type="submit"
                              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm"
                            >
                              Update
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowChangePassword(null);
                                setNewPassword('');
                              }}
                              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded text-sm"
                            >
                              Cancel
                            </button>
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {users.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-500">
                No users found.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && isSysAdmin && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Role Access</h2>
              <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                Tick the screens and tabs each role can access. System Administrators always have full
                access. Changes take effect on a user's next page load (a role change still requires re-login).
              </p>
            </div>
            <button
              onClick={handleSaveRolePermissions}
              disabled={!rolePermsDirty || rolePermsSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            >
              {rolePermsSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {rolePermsLoading && (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          )}

          {rolePerms && !rolePermsLoading && (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                      Screen / Tab
                    </th>
                    {rolePerms.roles.map((role) => (
                      <th
                        key={role.value}
                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-l border-gray-200"
                      >
                        {role.label}
                        {role.always && <div className="text-[10px] normal-case text-gray-400 font-normal">always</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...new Set(rolePerms.items.map((i) => i.group))].map((group) => (
                    <React.Fragment key={group}>
                      <tr className="bg-gray-50">
                        <td
                          colSpan={rolePerms.roles.length + 1}
                          className="px-4 py-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200"
                        >
                          {group}
                        </td>
                      </tr>
                      {rolePerms.items.filter((i) => i.group === group).map((item) => (
                        <tr key={item.key} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-sm text-gray-800 border-b border-gray-100">
                            {item.label}
                          </td>
                          {rolePerms.roles.map((role) => (
                            <td key={role.value} className="px-4 py-2.5 text-center border-b border-l border-gray-100">
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60"
                                checked={role.always ? true : !!rolePerms.matrix[role.value]?.[item.key]}
                                disabled={role.always}
                                onChange={() => toggleRolePerm(role.value, item.key)}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-400 mt-3">
                Settings and Data are always restricted to System Administrators and are not listed here.
                The Help &amp; Feedback screen is available to everyone.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && isSysAdmin && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Email Communications Log</h2>
            <button
              onClick={() => fetchEmailLog(emailLogPage)}
              disabled={emailLogLoading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50 flex items-center"
            >
              {emailLogLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </>
              )}
            </button>
          </div>

          {/* Email config status banner */}
          {emailConfigured !== null && (
            <div className={`mb-4 px-4 py-3 rounded flex items-center ${
              emailConfigured
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              <div className={`w-2.5 h-2.5 rounded-full mr-3 ${emailConfigured ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm font-medium">
                {emailConfigured
                  ? 'Email service is configured and active'
                  : 'Email service is not configured — emails will not be sent'}
              </span>
            </div>
          )}

          {/* Email log table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {emailLog.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <tr
                      onClick={() => setExpandedLogRow(expandedLogRow === entry.id ? null : entry.id)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDateTime(entry.sent_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 max-w-[200px] truncate" title={entry.to_address}>
                        {entry.to_address}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-[250px] truncate" title={entry.subject}>
                        {entry.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {entry.notification_type || 'general'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          entry.status === 'success'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {entry.status}
                        </span>
                      </td>
                    </tr>
                    {expandedLogRow === entry.id && (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 bg-gray-50">
                          <div className="text-sm space-y-1">
                            {entry.message_id && (
                              <div>
                                <span className="font-medium text-gray-700">Message ID:</span>{' '}
                                <span className="text-gray-600 font-mono text-xs">{entry.message_id}</span>
                              </div>
                            )}
                            {entry.error_message && (
                              <div>
                                <span className="font-medium text-red-700">Error:</span>{' '}
                                <span className="text-red-600">{entry.error_message}</span>
                              </div>
                            )}
                            {!entry.message_id && !entry.error_message && (
                              <span className="text-gray-500 italic">No additional details</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {emailLog.length === 0 && !emailLogLoading && (
              <div className="text-center py-8 text-gray-500">
                No email logs found. Emails will be logged here once sent.
              </div>
            )}
          </div>

          {/* Pagination */}
          {emailLogPagination && emailLogPagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing page {emailLogPagination.page} of {emailLogPagination.totalPages} ({emailLogPagination.total} total)
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => fetchEmailLog(emailLogPage - 1)}
                  disabled={emailLogPage <= 1 || emailLogLoading}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchEmailLog(emailLogPage + 1)}
                  disabled={emailLogPage >= emailLogPagination.totalPages || emailLogLoading}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Feedback Tab */}
      {activeTab === 'feedback' && isSysAdmin && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Feedback Submissions</h2>
            <button
              onClick={() => fetchFeedbackLog(feedbackLogPage)}
              disabled={feedbackLogLoading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50 flex items-center"
            >
              {feedbackLogLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </>
              )}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email Sent</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {feedbackLog.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDateTime(entry.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {entry.username}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                      <div className="whitespace-pre-wrap break-words">{entry.message}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        entry.email_sent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {entry.email_sent ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {feedbackLog.length === 0 && !feedbackLogLoading && (
              <div className="text-center py-8 text-gray-500">
                No feedback submissions yet.
              </div>
            )}
          </div>

          {feedbackLogPagination && feedbackLogPagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing page {feedbackLogPagination.page} of {feedbackLogPagination.totalPages} ({feedbackLogPagination.total} total)
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => fetchFeedbackLog(feedbackLogPage - 1)}
                  disabled={feedbackLogPage <= 1 || feedbackLogLoading}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchFeedbackLog(feedbackLogPage + 1)}
                  disabled={feedbackLogPage >= feedbackLogPagination.totalPages || feedbackLogLoading}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Versions Tab */}
      {activeTab === 'versions' && isSysAdmin && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">App Versions</h2>

          {/* Add Version Form */}
          <form onSubmit={handleAddVersion} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Add New Version</h3>
            <div className="flex flex-wrap gap-3">
              <input
                type="text"
                value={newVersion.version}
                onChange={(e) => setNewVersion({ ...newVersion, version: e.target.value })}
                placeholder="Version (e.g. 1.2.0)"
                required
                maxLength={20}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
              />
              <input
                type="text"
                value={newVersion.notes}
                onChange={(e) => setNewVersion({ ...newVersion, notes: e.target.value })}
                placeholder="Change notes (optional)"
                className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={!newVersion.version.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
              >
                Add Version
              </button>
            </div>
          </form>

          {/* Versions Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added By</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {versions.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{v.version}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-md">{v.notes || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(v.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{v.created_by}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleDeleteVersion(v.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {versions.length === 0 && !versionsLoading && (
              <div className="text-center py-8 text-gray-500">
                No versions added yet. Add your first version above.
              </div>
            )}

            {versionsLoading && (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            )}
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && isSysAdmin && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Audit Log</h2>
            <button
              onClick={() => fetchAuditLog({ page: auditLogPage })}
              disabled={auditLogLoading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50 flex items-center"
            >
              {auditLogLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </>
              )}
            </button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') fetchAuditLog({ page: 1, search: auditSearch });
                }}
                placeholder="Search username, action, details, IP..."
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={auditActionFilter}
              onChange={(e) => {
                setAuditActionFilter(e.target.value);
                fetchAuditLog({ page: 1, action: e.target.value });
              }}
              className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Actions</option>
              {auditActions.map((action) => (
                <option key={action} value={action}>{action.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <button
              onClick={() => fetchAuditLog({ page: 1, search: auditSearch })}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
            >
              Search
            </button>
            {(auditSearch || auditActionFilter) && (
              <button
                onClick={() => {
                  setAuditSearch('');
                  setAuditActionFilter('');
                  fetchAuditLog({ page: 1, search: '', action: '' });
                }}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm"
              >
                Clear
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => {
                      const newDir = auditSortBy === 'timestamp' && auditSortDir === 'desc' ? 'asc' : 'desc';
                      setAuditSortBy('timestamp');
                      setAuditSortDir(newDir);
                      fetchAuditLog({ page: 1, sortBy: 'timestamp', sortDir: newDir });
                    }}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  >
                    Date/Time {auditSortBy === 'timestamp' && (auditSortDir === 'desc' ? '\u2193' : '\u2191')}
                  </th>
                  <th
                    onClick={() => {
                      const newDir = auditSortBy === 'username' && auditSortDir === 'asc' ? 'desc' : 'asc';
                      setAuditSortBy('username');
                      setAuditSortDir(newDir);
                      fetchAuditLog({ page: 1, sortBy: 'username', sortDir: newDir });
                    }}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  >
                    User {auditSortBy === 'username' && (auditSortDir === 'desc' ? '\u2193' : '\u2191')}
                  </th>
                  <th
                    onClick={() => {
                      const newDir = auditSortBy === 'action' && auditSortDir === 'asc' ? 'desc' : 'asc';
                      setAuditSortBy('action');
                      setAuditSortDir(newDir);
                      fetchAuditLog({ page: 1, sortBy: 'action', sortDir: newDir });
                    }}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  >
                    Action {auditSortBy === 'action' && (auditSortDir === 'desc' ? '\u2193' : '\u2191')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  <th
                    onClick={() => {
                      const newDir = auditSortBy === 'ip_address' && auditSortDir === 'asc' ? 'desc' : 'asc';
                      setAuditSortBy('ip_address');
                      setAuditSortDir(newDir);
                      fetchAuditLog({ page: 1, sortBy: 'ip_address', sortDir: newDir });
                    }}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  >
                    IP Address {auditSortBy === 'ip_address' && (auditSortDir === 'desc' ? '\u2193' : '\u2191')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditLog.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDateTime(entry.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {entry.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        entry.action.includes('fail') ? 'bg-red-100 text-red-800' :
                        entry.action.includes('login') ? 'bg-green-100 text-green-800' :
                        entry.action.includes('logout') ? 'bg-gray-100 text-gray-800' :
                        entry.action.includes('created') || entry.action.includes('sync') ? 'bg-blue-100 text-blue-800' :
                        entry.action.includes('deleted') ? 'bg-red-100 text-red-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {entry.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={entry.details}>
                      {entry.details}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {entry.ip_address}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {auditLog.length === 0 && !auditLogLoading && (
              <div className="text-center py-8 text-gray-500">
                No audit log entries found.
              </div>
            )}
          </div>

          {auditLogPagination && auditLogPagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing page {auditLogPagination.page} of {auditLogPagination.totalPages} ({auditLogPagination.total} total)
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => fetchAuditLog({ page: auditLogPage - 1 })}
                  disabled={auditLogPage <= 1 || auditLogLoading}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchAuditLog({ page: auditLogPage + 1 })}
                  disabled={auditLogPage >= auditLogPagination.totalPages || auditLogLoading}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Settings;
